/**
 * Mappers for Create Chat functionality
 * Maps GraphQL types to domain types for type safety and consistency
 */

import type {
  BuyerSelectOption,
  UserSelectOption,
  SupplierUserSelectOption,
  BuyerUserSelectOption,
  SubCatalogOption,
  OrderPreference,
  CreatedChatResponse,
  UserMetadata,
  BuyerMetadata,
} from '@choco-admin/types';
import { OrderPreferenceType, CreateChatType } from '@choco-admin/types';

// Type guards for GraphQL response types
const isValidChatType = (
  type: string | null | undefined
): type is CreateChatType => {
  return type === 'ordering' || type === 'support';
};

/**
 * Maps GraphQL order preference to domain OrderPreference array
 */
export const mapOrderPreferences = (
  orderPreference: any
): OrderPreference[] => {
  if (!orderPreference) return [];

  const preferences: OrderPreference[] = [];

  if (orderPreference.whatsappNumber) {
    preferences.push({
      type: OrderPreferenceType.WHATSAPP,
      value: orderPreference.whatsappNumber,
    });
  }

  if (orderPreference.smsNumber) {
    preferences.push({
      type: OrderPreferenceType.SMS,
      value: orderPreference.smsNumber,
    });
  }

  if (orderPreference.faxNumber) {
    preferences.push({
      type: OrderPreferenceType.FAX,
      value: orderPreference.faxNumber,
    });
  }

  if (orderPreference.emailAddress) {
    preferences.push({
      type: OrderPreferenceType.EMAIL,
      value: orderPreference.emailAddress,
    });
  }

  return preferences;
};

/**
 * Maps GraphQL buyer search result to BuyerSelectOption
 */
export const mapToBuyerSelectOption = (buyer: any): BuyerSelectOption => {
  const { fullAddress, name, displayName, referenceId } = buyer;
  const metadata: BuyerMetadata = {
    fullAddress: fullAddress || '',
    name: name || displayName || '',
    referenceId: referenceId || '',
  };

  const buyerName = buyer.displayName || buyer.name || `Buyer ${buyer.id}`;
  const label = referenceId ? `${buyerName} - ${referenceId}` : buyerName;

  return {
    value: buyer.id,
    label,
    metaData: metadata,
  };
};

/**
 * Maps GraphQL supplier search result to UserSelectOption
 */
export const mapToSupplierSelectOption = (supplier: any): UserSelectOption => {
  const { botUser, referenceId } = supplier;
  const orderPreferences = mapOrderPreferences(botUser?.orderPreference);

  const metadata: UserMetadata = {
    name: supplier.name || botUser?.name,
    supplierId: supplier.id,
    phone: botUser?.phone,
    email: botUser?.email,
    orderPreferences,
    supplier: true,
    referenceId: referenceId || '',
  };

  const supplierName =
    supplier.name || botUser?.name || `Supplier ${supplier.id}`;
  const label = referenceId ? `${supplierName} - ${referenceId}` : supplierName;

  return {
    value: supplier.id,
    label,
    metaData: metadata,
  };
};

/**
 * Maps GraphQL user search result to SupplierUserSelectOption or BuyerUserSelectOption
 */
export const mapToUserSelectOption = (
  user: any,
  isSupplier: boolean
): SupplierUserSelectOption | BuyerUserSelectOption => {
  const orderPreferences = mapOrderPreferences(user.orderPreference);

  const baseMetadata: UserMetadata = {
    name: user.name,
    phone: user.phone,
    email: user.email,
    orderPreferences,
    supplier: isSupplier,
  };

  const baseOption = {
    value: user.id,
    label: user.name || `User ${user.id}`,
  };

  if (isSupplier) {
    return {
      ...baseOption,
      metaData: {
        ...baseMetadata,
        supplier: true,
      },
    } as SupplierUserSelectOption;
  }
  return {
    ...baseOption,
    metaData: {
      ...baseMetadata,
      supplier: false,
    },
  } as BuyerUserSelectOption;
};

/**
 * Maps GraphQL buyer user result to BuyerUserSelectOption
 */
export const mapToBuyerUserSelectOption = (
  buyerUser: any
): BuyerUserSelectOption | null => {
  if (!buyerUser?.node) {
    return null;
  }

  const user = buyerUser.node;
  if (!user?.id) {
    return null;
  }

  const metadata: UserMetadata & { supplier: false } = {
    name: user.name || '',
    phone: user.phone || '',
    email: user.email || '',
    supplier: false,
  };

  return {
    value: user.id,
    label: user.name || `User ${user.id}`,
    metaData: metadata,
  };
};

/**
 * Maps GraphQL sub-catalog result to SubCatalogOption
 */
export const mapToSubCatalogOption = (subCatalog: any): SubCatalogOption => {
  return {
    value: subCatalog.id,
    label: subCatalog.name || `Sub-Catalog ${subCatalog.id}`,
    supplierId: subCatalog.supplierEntityId,
    description: subCatalog.description,
  };
};

/**
 * Maps GraphQL created chat response to CreatedChatResponse domain type
 */
export const mapToCreatedChatResponse = (
  chatData: any
): CreatedChatResponse => {
  return {
    id: chatData.id,
    chatName: chatData.chatName,
    restaurantName: chatData.restaurantName,
    type: isValidChatType(chatData.type)
      ? chatData.type
      : CreateChatType.ORDERING,
    enabled: Boolean(chatData.enabled),
    createdAt: chatData.createdAt || new Date().toISOString(),
    buyer: chatData.buyer
      ? {
          id: chatData.buyer.id,
          name: chatData.buyer.displayName || chatData.buyer.name || '',
        }
      : undefined,
    supplier: chatData.supplierEntity
      ? {
          id: chatData.supplierEntity.id,
          name: chatData.supplierEntity.name || '',
        }
      : undefined,
  };
};

/**
 * Maps domain CreateChatInput to GraphQL ChatCreateInput
 */
export const mapToGraphQLChatCreateInput = (input: any) => {
  return {
    chatName: input.chatName || null,
    restaurantName: input.restaurantName || null,
    deliveryAddress: input.deliveryAddress || null,
    customerNumber: input.customerNumber || null,
    additionalEmails: input.additionalEmails
      ? input.additionalEmails
          .split(',')
          .map((email: string) => email.trim())
          .filter(Boolean)
      : [],
    type: input.type,
    supplierEntityId: input.supplierId,
    buyerId: input.buyerId || null,
    userIds: input.userIds || [],
    subCatalogId: input.subCatalogId || null,
    isPuppy: input.isPuppy || false,
    createdContext: input.createdContext,
    customerLevelMovAmount: input.customerLevelMovAmount || null,
    customerLevelMovCurrency: input.customerLevelMovCurrency || null,
  };
};

/**
 * Helper function to extract user IDs from form data
 */
export const extractUserIds = (formData: any): string[] => {
  const userIds: string[] = [];

  // Add supplier ID
  if (formData.supplier?.value) {
    userIds.push(formData.supplier.value);
  }

  // Add supplier users
  if (formData.supplierUsers) {
    formData.supplierUsers.forEach((user: any) => {
      if (user.value) {
        userIds.push(user.value);
      }
    });
  }

  // Add buyer users
  if (formData.buyerUsers) {
    formData.buyerUsers.forEach((user: any) => {
      if (user.value) {
        userIds.push(user.value);
      }
    });
  }

  return userIds;
};

/**
 * Helper function to extract sub-catalog IDs from form data
 */
export const extractSubCatalogIds = (
  subCatalogs: SubCatalogOption[]
): string[] => {
  return subCatalogs?.map((catalog) => catalog.value) || [];
};
